import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

export type GpsFix = {
  lat: number;
  lng: number;
  accuracyM: number | null;
};

export type GpsWatchHandle = { stop: () => void };

export function isNativeGeolocation(): boolean {
  return Capacitor.isNativePlatform();
}

function fromCapacitor(position: {
  coords: { latitude: number; longitude: number; accuracy: number };
}): GpsFix {
  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracyM: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null,
  };
}

function fromBrowser(position: GeolocationPosition): GpsFix {
  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracyM: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null,
  };
}

export async function requestGpsPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return true;
  let status = await Geolocation.checkPermissions();
  if (status.location !== "granted" && status.coarseLocation !== "granted") {
    status = await Geolocation.requestPermissions();
  }
  return status.location === "granted" || status.coarseLocation === "granted";
}

export async function getGpsFix(timeoutMs = 15_000): Promise<GpsFix> {
  if (Capacitor.isNativePlatform()) {
    const granted = await requestGpsPermission();
    if (!granted) throw new Error("Location permission denied");
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: timeoutMs,
    });
    return fromCapacitor(position);
  }
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not available"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(fromBrowser(position)),
      (error) => reject(new Error(error.message || "Location unavailable")),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 5_000 },
    );
  });
}

export function watchGps(onFix: (fix: GpsFix) => void, onError: (message: string) => void): GpsWatchHandle {
  if (Capacitor.isNativePlatform()) {
    let cancelled = false;
    let watchId: string | undefined;
    void (async () => {
      try {
        const granted = await requestGpsPermission();
        if (cancelled) return;
        if (!granted) {
          onError("Location permission denied");
          return;
        }
        const id = await Geolocation.watchPosition({ enableHighAccuracy: true }, (position, error) => {
          if (cancelled) return;
          if (error) {
            onError(error.message || "Location unavailable");
            return;
          }
          if (position) onFix(fromCapacitor(position));
        });
        watchId = id;
        if (cancelled) {
          void Geolocation.clearWatch({ id });
        }
      } catch (error) {
        if (!cancelled) onError(error instanceof Error ? error.message : "Location unavailable");
      }
    })();
    return {
      stop: () => {
        cancelled = true;
        if (watchId) void Geolocation.clearWatch({ id: watchId });
      },
    };
  }

  if (!navigator.geolocation) {
    onError("Geolocation is not available");
    return { stop: () => {} };
  }
  const id = navigator.geolocation.watchPosition(
    (position) => onFix(fromBrowser(position)),
    (error) => onError(error.message || "Location unavailable"),
    { enableHighAccuracy: true, maximumAge: 2_000 },
  );
  return { stop: () => navigator.geolocation.clearWatch(id) };
}
